import React, { useState, useRef, useCallback, useEffect } from 'react';
// Manual trace overlay (Option 2) implemented inline below
import { Upload, Image as ImageIcon, Download, AlertCircle, CheckCircle, Loader, Pipette, Pencil, Eraser } from 'lucide-react';

interface PolygonExtractionOptions {
  color_type: 'auto' | 'red_white' | 'blue' | 'green';
  tighten: boolean;
  simplify: boolean;
  min_area_ratio: number;
  epsilon_factor: number;
  use_convex_hull?: boolean;
  hue_tolerance?: number; // new
  gap_strength?: number;  // new
  red_hex?: string;       // new: user-picked line color
  closure_mode?: 'directional' | 'isotropic';
}

interface PolygonResult {
  success: boolean;
  polygon?: number[][];
  metadata?: {
    num_points: number;
    area_pixels: number;
    perimeter_pixels: number;
    image_dimensions: {
      width: number;
      height: number;
    };
    extraction_options: PolygonExtractionOptions;
  };
  preview_image?: string;
  blue_outline_image?: string; // intermediary image fed to extractor
  recolor_image?: string;      // alias for backward/compatibility
  closed_gaps_image?: string;  // new: after morphological closing
  filled_mask_image?: string;  // new: after interior fill
  num_contours_found?: number;
  error?: string;
  error_type?: string;
}

interface PolygonExtractorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPolygonExtracted: (polygon: number[][], metadata: any) => void;
  title?: string;
  description?: string;
}

const PolygonExtractorModal: React.FC<PolygonExtractorModalProps> = ({
  isOpen,
  onClose,
  onPolygonExtracted,
  title = "Extract Area Boundary",
  description = "Upload a map image to automatically extract the area boundary polygon"
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const previewUrlRef = useRef<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [result, setResult] = useState<PolygonResult | null>(null);
  // Option 2 full-screen edit overlay
  const [isEditMode, setIsEditMode] = useState(false);
  // Base image for overlay (Blue Outline from backend)
  const [overlayBaseUrl, setOverlayBaseUrl] = useState<string | null>(null);
  const [overlayOriginal, setOverlayOriginal] = useState<boolean>(false);
  const [imgAspect, setImgAspect] = useState<number | null>(null);
  const [natW, setNatW] = useState<number | null>(null);
  const [natH, setNatH] = useState<number | null>(null);
  const [zoom, setZoom] = useState<number>(1);
  const initializedZoomRef = useRef<boolean>(false);
  const scrollWrapRef = useRef<HTMLDivElement | null>(null);
  const imageContainerRef = useRef<HTMLDivElement | null>(null);
  const baseImageRef = useRef<HTMLImageElement | null>(null);
  const drawingCanvasRef = useRef<HTMLCanvasElement | null>(null);
  // Store strokes (draw/erase) as arrays of points in display coordinates
  type Stroke = { points: { x: number; y: number }[]; mode: 'draw' | 'erase' };
  const [strokes, setStrokes] = useState<Stroke[]>([]);
  const [currentStroke, setCurrentStroke] = useState<Stroke | null>(null);
  const STROKE_WIDTH = 6; // px in display space
  const STROKE_COLOR = '#0000FF'; // match backend Blue Outline color for visual alignment
  const ERASER_PREVIEW_COLOR = '#ffffff';
  const [tool, setTool] = useState<'draw' | 'erase'>('draw');
  // Closure gating thresholds (relative to wall bbox)
  const CLOSURE_AREA_THRESHOLD = 0.25; // inside% >= 30% to consider plausibly closed
  const CLOSURE_MIN_COMPONENT_RATIO = 0.05; // ignore tiny blob closures (<5% of bbox)
  // Closure check status
  const [closureStatus, setClosureStatus] = useState<'idle' | 'checking' | 'closed' | 'open'>('idle');
  const [closureInfo, setClosureInfo] = useState<{ enclosedArea: number; areaRatio: number } | null>(null);
  // Image picker/magnifier state
  const imgRef = useRef<HTMLImageElement | null>(null);
  const imgContainerRef = useRef<HTMLDivElement | null>(null);
  const sourceCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const lensCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const [showLens, setShowLens] = useState(false);
  const [lensPos, setLensPos] = useState({ x: 0, y: 0 });
  const [pickedHex, setPickedHex] = useState<string | null>(null);
  const [pickingEnabled, setPickingEnabled] = useState(false);
  // Lens behavior constants
  const LENS_SIZE = 60;
  const LENS_ZOOM = 5;
  // Original behavior: no snap/min-delta smoothing
  const [options, setOptions] = useState<PolygonExtractionOptions>({
    color_type: 'auto',
    tighten: true,
    simplify: false,  // Changed to preserve detail by default
    min_area_ratio: 0.001,  // More sensitive: 0.1% instead of 1%
    epsilon_factor: 0.005,   // Much more detailed: 0.5% instead of 2%
    use_convex_hull: false,  // Option to merge multiple boundary segments
    hue_tolerance: 15,
    gap_strength: 1.0,
    red_hex: '#e74737',
    closure_mode: 'directional'
  });

  const fileInputRef = useRef<HTMLInputElement>(null);
  const dragRef = useRef<HTMLDivElement>(null);

  // Unified cancel: reset local state and bubble up to parent
  const handleCancelAll = useCallback(() => {
    setIsEditMode(false);
    setStrokes([]);
    setCurrentStroke(null);
    setOverlayBaseUrl(null);
    setClosureStatus('idle');
    setClosureInfo(null);
    resetState();
    onClose();
  }, [onClose]);

  const handleFileSelect = useCallback((file: File) => {
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    if (file.size > 10 * 1024 * 1024) { // 10MB limit
      alert('File size must be less than 10MB');
      return;
    }

    setSelectedFile(file);
    setResult(null);

    // Create preview URL
    const url = URL.createObjectURL(file);
    // Revoke any previous preview to avoid leaks
    if (previewUrlRef.current) {
      try { URL.revokeObjectURL(previewUrlRef.current); } catch {}
    }
    previewUrlRef.current = url;
    setPreviewUrl(url);
  setOverlayBaseUrl(null);
  setClosureStatus('checking');

    // Reset picker state
    setPickedHex(null);
    setShowLens(false);

    // Immediately open markup and kick off blue-outline fetch to avoid base modal flash
    setIsEditMode(true);
    initializedZoomRef.current = false;
    try {
      const fd = new FormData();
      fd.append('image', file);
      if (options.hue_tolerance !== undefined) fd.append('hue_tolerance', String(options.hue_tolerance));
      if (options.gap_strength !== undefined) fd.append('gap_strength', String(options.gap_strength));
      if (options.red_hex) fd.append('red_hex', options.red_hex);
      fd.append('transparent_bg', overlayOriginal ? 'true' : 'false');
      fetch('http://localhost:5001/blue-outline-file', { method: 'POST', body: fd })
        .then(r => r.json())
        .then((j: any) => {
          if (j && j.success && j.blue_outline_image) setOverlayBaseUrl(j.blue_outline_image);
        })
        .catch(() => {/* overlay shows spinner until available */});
    } catch {}
  }, []);

  // Auto-open handled in handleFileSelect to prevent base modal flash

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  }, [handleFileSelect]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

/*   const extractPolygon = async () => {
    if (!selectedFile) return;

    setIsProcessing(true);
    setResult(null);

    try {
      const formData = new FormData();
      formData.append('image', selectedFile);
      formData.append('color_type', options.color_type);
      formData.append('tighten', options.tighten.toString());
      formData.append('simplify', options.simplify.toString());
      formData.append('min_area_ratio', options.min_area_ratio.toString());
      formData.append('epsilon_factor', options.epsilon_factor.toString());
    formData.append('use_convex_hull', (options.use_convex_hull || false).toString());
  if (options.hue_tolerance !== undefined) formData.append('hue_tolerance', String(options.hue_tolerance));
  if (options.gap_strength !== undefined) formData.append('gap_strength', String(options.gap_strength));
  if (options.red_hex) formData.append('red_hex', options.red_hex);
  if (options.closure_mode) formData.append('closure_mode', options.closure_mode);

      const response = await fetch('http://localhost:5001/extract-polygon-file', {
        method: 'POST',
        body: formData
      });

      const result: PolygonResult = await response.json();
      setResult(result);

      if (result.success && result.polygon) {
        console.log('Polygon extracted successfully:', result);
      }

    } catch (error) {
      console.error('Error extracting polygon:', error);
      setResult({
        success: false,
        error: 'Failed to connect to polygon extraction service. Please ensure the service is running.',
        error_type: 'ConnectionError'
      });
    } finally {
      setIsProcessing(false);
    }
  }; */

  // Helpers to compute displayed image metrics and map to original pixels
  const getImageMetrics = () => {
    const imgEl = imgRef.current;
    const container = imgContainerRef.current;
    if (!imgEl || !container) return null;
    const rect = container.getBoundingClientRect();
    const naturalW = imgEl.naturalWidth || 1;
    const naturalH = imgEl.naturalHeight || 1;
    const contW = rect.width;
    const contH = rect.height;
    const scale = Math.min(contW / naturalW, contH / naturalH);
    const dispW = naturalW * scale;
    const dispH = naturalH * scale;
    const offX = (contW - dispW) / 2;
    const offY = (contH - dispH) / 2;
    return { naturalW, naturalH, contW, contH, scale, dispW, dispH, offX, offY };
  };

  // Update Blue Outline only when edit overlay is open
  useEffect(() => {
    if (!selectedFile || !isEditMode) return;
    setClosureStatus('checking');
    const fd = new FormData();
    fd.append('image', selectedFile);
    if (options.hue_tolerance !== undefined) fd.append('hue_tolerance', String(options.hue_tolerance));
    if (options.gap_strength !== undefined) fd.append('gap_strength', String(options.gap_strength));
    if (options.red_hex) fd.append('red_hex', options.red_hex);
    fd.append('transparent_bg', overlayOriginal ? 'true' : 'false');
    let cancelled = false;
    fetch('http://localhost:5001/blue-outline-file', { method: 'POST', body: fd })
      .then(r => r.json())
      .then((j: any) => {
        if (!cancelled && j && j.success && j.blue_outline_image) {
          setOverlayBaseUrl(j.blue_outline_image);
        }
      })
      .catch(() => {/* keep prior overlayBaseUrl */});
    return () => { cancelled = true; };
  }, [selectedFile, isEditMode, options.hue_tolerance, options.gap_strength, options.red_hex, overlayOriginal]);

  // Auto-run closure check when overlayBaseUrl updates (image ready) and edit mode is active
  useEffect(() => {
    if (isEditMode && overlayBaseUrl) {
      // defer to next tick so image paints
      const t = setTimeout(() => { checkClosure(); }, 0);
      return () => clearTimeout(t);
    }
  }, [isEditMode, overlayBaseUrl]);

  // Keep closure status updated while user draws/erases (light debounce)
  useEffect(() => {
    if (!isEditMode || !overlayBaseUrl) return;
    const t = setTimeout(() => { checkClosure(); }, 120);
    return () => clearTimeout(t);
  }, [isEditMode, overlayBaseUrl, strokes, currentStroke]);

  // Open overlay on demand and fetch Blue Outline once
  const openMarkup = useCallback(() => {
    if (!selectedFile) return;
    setIsEditMode(true);
    setOverlayBaseUrl(null);
    setClosureStatus('checking');
    initializedZoomRef.current = false; // allow first image load to set initial zoom
    try {
      const fd = new FormData();
      fd.append('image', selectedFile);
      if (options.hue_tolerance !== undefined) fd.append('hue_tolerance', String(options.hue_tolerance));
      if (options.gap_strength !== undefined) fd.append('gap_strength', String(options.gap_strength));
      if (options.red_hex) fd.append('red_hex', options.red_hex);
      fd.append('transparent_bg', overlayOriginal ? 'true' : 'false');
      fetch('http://localhost:5001/blue-outline-file', { method: 'POST', body: fd })
        .then(r => r.json())
        .then((j: any) => {
          if (j && j.success && j.blue_outline_image) {
            setOverlayBaseUrl(j.blue_outline_image);
          }
        })
        .catch(() => {/* overlay will show spinner until available */});
    } catch {}
  }, [selectedFile, options.hue_tolerance, options.gap_strength, options.red_hex, overlayOriginal]);

  const ensureSourceCanvasDrawn = () => {
    const imgEl = imgRef.current;
    const canvas = sourceCanvasRef.current;
    if (!imgEl) return;
    if (!canvas) return;
    if (canvas.width !== imgEl.naturalWidth || canvas.height !== imgEl.naturalHeight) {
      canvas.width = imgEl.naturalWidth;
      canvas.height = imgEl.naturalHeight;
    }
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(imgEl, 0, 0, canvas.width, canvas.height);
    }
  };

  // Helper: draw lens content and position at container coordinates (cx, cy)
  const drawLensAt = (cx: number, cy: number) => {
    const container = imgContainerRef.current;
    const lens = lensCanvasRef.current;
    if (!container || !lens) return;
    const metrics = getImageMetrics();
    if (!metrics) return;
    const { scale, offX, offY } = metrics as any;
    ensureSourceCanvasDrawn();
    const src = sourceCanvasRef.current!;
    const sctx = src.getContext('2d');
    const lctx = lens.getContext('2d');
    if (!sctx || !lctx) return;
    const ix = Math.round((cx - offX) / scale);
    const iy = Math.round((cy - offY) / scale);
    const zoom = LENS_ZOOM;
    const sw = Math.max(1, Math.round(LENS_SIZE / zoom));
    const sh = sw;
    const sx = Math.max(0, Math.min(src.width - sw, ix - Math.floor(sw / 2)));
    const sy = Math.max(0, Math.min(src.height - sh, iy - Math.floor(sh / 2)));
    setLensPos({ x: cx - LENS_SIZE / 2, y: cy - LENS_SIZE / 2 });
    lctx.imageSmoothingEnabled = true;
    lctx.clearRect(0, 0, lens.width, lens.height);
    lctx.drawImage(src, sx, sy, sw, sh, 0, 0, lens.width, lens.height);
    lctx.strokeStyle = 'rgba(59,130,246,0.9)';
    lctx.lineWidth = 1;
    lctx.beginPath();
    lctx.moveTo(lens.width / 2, 0);
    lctx.lineTo(lens.width / 2, lens.height);
    lctx.moveTo(0, lens.height / 2);
    lctx.lineTo(lens.width, lens.height / 2);
    lctx.stroke();
  };

  const handleImageMouseMove = (e: React.MouseEvent) => {
    if (!pickingEnabled) return;
    const container = imgContainerRef.current;
    const lens = lensCanvasRef.current;
    if (!container || !lens) return;
    const metrics = getImageMetrics();
    if (!metrics) return;
    const { scale, offX, offY, dispW, dispH } = metrics as any;
    const rect = container.getBoundingClientRect();
    const rawCx = e.clientX - rect.left;
    const rawCy = e.clientY - rect.top;

    // Show lens only when within actual displayed image area
    if (rawCx < offX || rawCy < offY || rawCx > offX + dispW || rawCy > offY + dispH) {
      setShowLens(false);
      return;
    }
    setShowLens(true);
    // Original: update lens position continuously without snap/min-delta
    const cx = rawCx;
    const cy = rawCy;
    setLensPos({ x: cx - LENS_SIZE / 2, y: cy - LENS_SIZE / 2 });

    // Draw zoomed area into lens
    ensureSourceCanvasDrawn();
    const src = sourceCanvasRef.current!;
    const sctx = src.getContext('2d');
    const lctx = lens.getContext('2d');
    if (!sctx || !lctx) return;
    const zoom = LENS_ZOOM;

    // Map container coords to original image pixel coords
    const ix = Math.round((cx - offX) / scale);
    const iy = Math.round((cy - offY) / scale);
    const sw = Math.max(1, Math.round(LENS_SIZE / zoom));
    const sh = sw;
    const sx = Math.max(0, Math.min(src.width - sw, ix - Math.floor(sw / 2)));
    const sy = Math.max(0, Math.min(src.height - sh, iy - Math.floor(sh / 2)));

    lctx.imageSmoothingEnabled = true;
    lctx.clearRect(0, 0, lens.width, lens.height);
    lctx.drawImage(src, sx, sy, sw, sh, 0, 0, lens.width, lens.height);
    // Crosshair
    lctx.strokeStyle = 'rgba(59,130,246,0.9)';
    lctx.lineWidth = 1;
    lctx.beginPath();
    lctx.moveTo(lens.width / 2, 0);
    lctx.lineTo(lens.width / 2, lens.height);
    lctx.moveTo(0, lens.height / 2);
    lctx.lineTo(lens.width, lens.height / 2);
    lctx.stroke();
  };

  const handleImageMouseLeave = () => {
    setShowLens(false);
  };

  const handleImageClickPick = (e: React.MouseEvent) => {
    if (!pickingEnabled) return;
    ensureSourceCanvasDrawn();
    const metrics = getImageMetrics();
    const container = imgContainerRef.current;
    if (!metrics || !container) return;
    const { scale, offX, offY, dispW, dispH } = metrics as any;
    const rect = container.getBoundingClientRect();
    // Use click position to map to image coords
    const cx = e.clientX - rect.left;
    const cy = e.clientY - rect.top;
    // Ignore clicks outside displayed image bounds
    if (cx < offX || cy < offY || cx > offX + dispW || cy > offY + dispH) return;
    const ix = Math.max(0, Math.min(Math.round((cx - offX) / scale), (imgRef.current?.naturalWidth || 1) - 1));
    const iy = Math.max(0, Math.min(Math.round((cy - offY) / scale), (imgRef.current?.naturalHeight || 1) - 1));
    const src = sourceCanvasRef.current!;
    const ctx = src.getContext('2d');
    if (!ctx) return;
    const data = ctx.getImageData(ix, iy, 1, 1).data;
    const r = data[0], g = data[1], b = data[2];
    const hex = '#' + [r, g, b].map(v => v.toString(16).padStart(2, '0')).join('');
    setPickedHex(hex);
    setOptions(prev => ({ ...prev, red_hex: hex }));
    // Auto-exit picking mode after selecting a color
    setPickingEnabled(false);
    setShowLens(false);
  };

  const handleConfirm = () => {
    if (result && result.success && result.polygon && result.metadata) {
      onPolygonExtracted(result.polygon, result.metadata);
      onClose();
    }
  };

  const downloadPolygon = () => {
    if (!result || !result.polygon) return;

    const data = {
      polygon: result.polygon,
      metadata: result.metadata,
      extracted_at: new Date().toISOString()
    };

    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `polygon_${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const resetState = () => {
    // Revoke preview URL if present
    if (previewUrlRef.current) {
      try { URL.revokeObjectURL(previewUrlRef.current); } catch {}
      previewUrlRef.current = null;
    }
    setSelectedFile(null);
    setPreviewUrl(null);
    setResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // When parent closes the modal (isOpen -> false), wipe internal state so reopening starts fresh
  useEffect(() => {
    if (!isOpen) {
      setIsEditMode(false);
      setStrokes([]);
      setCurrentStroke(null);
      setOverlayBaseUrl(null);
      setOverlayOriginal(false);
      setClosureStatus('idle');
      setClosureInfo(null);
      resetState();
    }
  }, [isOpen]);

  // Resize drawing canvas to match container each frame it changes
  useEffect(() => {
    const cont = imageContainerRef.current;
    const canvas = drawingCanvasRef.current;
    if (!cont || !canvas) return;
    // Canvas should match container size (scaled by zoom)
    const rect = cont.getBoundingClientRect();
    const w = Math.max(1, Math.floor(rect.width));
    const h = Math.max(1, Math.floor(rect.height));
    if (canvas.width !== w || canvas.height !== h) {
      canvas.width = w; canvas.height = h;
    }
    // redraw strokes
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.lineWidth = STROKE_WIDTH;
      for (const s of strokes) {
        if (s.points.length < 2) continue;
        ctx.strokeStyle = s.mode === 'draw' ? STROKE_COLOR : ERASER_PREVIEW_COLOR;
        ctx.beginPath();
        ctx.moveTo(s.points[0].x, s.points[0].y);
        for (let i = 1; i < s.points.length; i++) ctx.lineTo(s.points[i].x, s.points[i].y);
        ctx.stroke();
      }
      // draw current stroke
      if (currentStroke && currentStroke.points.length > 1) {
        ctx.strokeStyle = currentStroke.mode === 'draw' ? STROKE_COLOR : ERASER_PREVIEW_COLOR;
        ctx.beginPath();
        ctx.moveTo(currentStroke.points[0].x, currentStroke.points[0].y);
        for (let i = 1; i < currentStroke.points.length; i++) ctx.lineTo(currentStroke.points[i].x, currentStroke.points[i].y);
        ctx.stroke();
      }
    }
  }, [strokes, currentStroke, isEditMode, selectedFile, previewUrl, overlayBaseUrl, zoom]);

  // Helpers for mapping container coords to natural pixels (overlay)
  const getOverlayMetrics = () => {
    const imgEl = baseImageRef.current;
    const container = imageContainerRef.current;
    if (!imgEl || !container) return null;
    const rect = container.getBoundingClientRect();
    const naturalW = imgEl.naturalWidth || 1;
    const naturalH = imgEl.naturalHeight || 1;
    const contW = rect.width;
    const contH = rect.height;
    const scale = Math.min(contW / naturalW, contH / naturalH);
    const dispW = naturalW * scale;
    const dispH = naturalH * scale;
    const offX = (contW - dispW) / 2;
    const offY = (contH - dispH) / 2;
    return { naturalW, naturalH, contW, contH, scale, dispW, dispH, offX, offY };
  };

  const startDraw = (clientX: number, clientY: number) => {
    const cont = imageContainerRef.current;
    if (!cont) return;
    const rect = cont.getBoundingClientRect();
    const p = { x: clientX - rect.left, y: clientY - rect.top };
    setCurrentStroke({ points: [p], mode: tool });
  };

  const continueDraw = (clientX: number, clientY: number) => {
    if (!currentStroke) return;
    const cont = imageContainerRef.current;
    if (!cont) return;
    const rect = cont.getBoundingClientRect();
    const p = { x: clientX - rect.left, y: clientY - rect.top };
    const last = currentStroke.points[currentStroke.points.length - 1];
    if (Math.hypot(p.x - last.x, p.y - last.y) < 2) return;
    setCurrentStroke({ ...currentStroke, points: [...currentStroke.points, p] });
  };

  const endDraw = () => {
    if (currentStroke && currentStroke.points.length > 1) {
      setStrokes(prev => [...prev, currentStroke]);
    }
    setCurrentStroke(null);
    // Re-evaluate closure after finishing a stroke
    setTimeout(() => { checkClosure(); }, 0);
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.setPointerCapture(e.pointerId);
    startDraw(e.clientX, e.clientY);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    e.stopPropagation();
    continueDraw(e.clientX, e.clientY);
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    e.stopPropagation();
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch {}
    endDraw();
  };

  const cancelEdit = () => {
    setIsEditMode(false);
    setStrokes([]);
    setCurrentStroke(null);
    initializedZoomRef.current = false; // reset for next session
    setClosureStatus('idle');
    setClosureInfo(null);
  };

  // Undo: remove current stroke if drawing, else pop last stroke
  const handleUndo = useCallback(() => {
    if (currentStroke && currentStroke.points.length > 0) {
      setCurrentStroke(null);
      // current stroke cancelled; re-check closure
      setTimeout(() => { checkClosure(); }, 0);
      return;
    }
    setStrokes(prev => {
      const next = prev.length > 0 ? prev.slice(0, -1) : prev;
      // re-check after state updates
      setTimeout(() => { checkClosure(); }, 0);
      return next as typeof prev;
    });
  }, [currentStroke]);

  // Keyboard shortcut: Ctrl/Cmd+Z to undo while editing
  useEffect(() => {
    if (!(isOpen && isEditMode)) return;
    const onKey = (e: KeyboardEvent) => {
      const isUndo = (e.ctrlKey || e.metaKey) && (e.key === 'z' || e.key === 'Z');
      if (isUndo) {
        e.preventDefault();
        handleUndo();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, isEditMode, handleUndo]);

  // Lock body scroll when edit overlay is open, to prevent background scrolling on touch
  useEffect(() => {
    if (!isOpen || !isEditMode) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const prevent = (e: Event) => { e.preventDefault(); };
    // Ensure non-passive to allow preventDefault on touch
    window.addEventListener('touchmove', prevent, { passive: false });
    window.addEventListener('wheel', prevent, { passive: false } as any);
    return () => {
      window.removeEventListener('touchmove', prevent as any);
      window.removeEventListener('wheel', prevent as any);
      document.body.style.overflow = original;
    };
  }, [isOpen, isEditMode]);

  const confirmMaskAndProceed = async () => {
    if (!selectedFile || !previewUrl) { setIsEditMode(false); return; }
    // If the user didn't draw anything, run the normal extraction
/*     if (strokes.length === 0 && !currentStroke) {
      setIsEditMode(false);
      await extractPolygon();
      return;
    } */
  const metrics = getOverlayMetrics();
    const imgEl = baseImageRef.current;
    if (!metrics || !imgEl) { setIsEditMode(false); return; }
    const { naturalW, naturalH, offX, offY, scale } = metrics as any;
    // Build mask at natural resolution
    const maskCanvas = document.createElement('canvas');
    maskCanvas.width = Math.round(naturalW);
    maskCanvas.height = Math.round(naturalH);
    const mctx = maskCanvas.getContext('2d');
    if (!mctx) { setIsEditMode(false); return; }
    mctx.clearRect(0, 0, maskCanvas.width, maskCanvas.height);
    mctx.fillStyle = '#000';
    mctx.fillRect(0, 0, maskCanvas.width, maskCanvas.height);
    // Merge Blue Outline (overlay image) into mask so those pixels count as boundary
    try {
      if (baseImageRef.current && overlayBaseUrl) {
        const tcan = document.createElement('canvas');
        tcan.width = Math.round(naturalW);
        tcan.height = Math.round(naturalH);
        const tctx = tcan.getContext('2d');
        if (tctx) {
          tctx.drawImage(baseImageRef.current, 0, 0, tcan.width, tcan.height);
          const imgData = tctx.getImageData(0, 0, tcan.width, tcan.height);
          const data = imgData.data;
          const maskData = mctx.getImageData(0, 0, maskCanvas.width, maskCanvas.height);
          const md = maskData.data;
          for (let i = 0; i < data.length; i += 4) {
            const r = data[i];
            const g = data[i + 1];
            const b = data[i + 2];
            // Blue Outline pixels are pure blue RGB(0,0,255)
            if (r === 0 && g === 0 && b === 255) {
              md[i] = 255; md[i + 1] = 255; md[i + 2] = 255; md[i + 3] = 255;
            }
          }
          mctx.putImageData(maskData, 0, 0);
        }
      }
    } catch {}
    mctx.lineCap = 'round';
    mctx.lineJoin = 'round';
    // Scale stroke width to natural pixels
    const natStroke = Math.max(2, Math.round(STROKE_WIDTH / Math.max(scale, 1e-6)));
    mctx.lineWidth = natStroke;
    const drawStrokeNat = (pts: { x: number; y: number }[], color: '#fff' | '#000') => {
      if (pts.length < 2) return;
      mctx.beginPath();
      // Map display to natural
      const mapPt = (p: { x: number; y: number }) => ({
        x: (p.x - offX) / scale,
        y: (p.y - offY) / scale
      });
      const p0 = mapPt(pts[0]);
      mctx.moveTo(p0.x, p0.y);
      for (let i = 1; i < pts.length; i++) {
        const pi = mapPt(pts[i]);
        mctx.lineTo(pi.x, pi.y);
      }
      mctx.strokeStyle = color;
      mctx.stroke();
    };
    // Apply strokes in order; draw adds white, erase paints black (removes)
    for (const s of strokes) drawStrokeNat(s.points, s.mode === 'draw' ? '#fff' : '#000');
    if (currentStroke) drawStrokeNat(currentStroke.points, currentStroke.mode === 'draw' ? '#fff' : '#000');

    // Convert mask canvas to Blob
    const blob: Blob = await new Promise(res => maskCanvas.toBlob(b => res(b || new Blob()), 'image/png'));

    setIsProcessing(true);
    setResult(null);
    try {
      const formData = new FormData();
      formData.append('image', selectedFile);
      formData.append('user_mask', blob, 'mask.png');
      formData.append('color_type', options.color_type);
      formData.append('tighten', options.tighten.toString());
      formData.append('simplify', options.simplify.toString());
      formData.append('min_area_ratio', options.min_area_ratio.toString());
      formData.append('epsilon_factor', options.epsilon_factor.toString());
      formData.append('use_convex_hull', (options.use_convex_hull || false).toString());
      if (options.hue_tolerance !== undefined) formData.append('hue_tolerance', String(options.hue_tolerance));
      if (options.gap_strength !== undefined) formData.append('gap_strength', String(options.gap_strength));
      if (options.red_hex) formData.append('red_hex', options.red_hex);
      if (options.closure_mode) formData.append('closure_mode', options.closure_mode);

      const response = await fetch('http://localhost:5001/extract-polygon-file', {
        method: 'POST',
        body: formData
      });
      const result: PolygonResult = await response.json();
      setResult(result);
      setIsEditMode(false);
      setStrokes([]);
      setCurrentStroke(null);
    } catch (error) {
      console.error('Error extracting polygon (manual mask):', error);
      setResult({
        success: false,
        error: 'Failed to connect to polygon extraction service. Please ensure the service is running.',
        error_type: 'ConnectionError'
      });
    } finally {
      setIsProcessing(false);
    }
  };

  // Build a downscaled mask of the current overlay (blue outline + strokes) and detect if there's a significant enclosed area.
  const checkClosure = useCallback(async () => {
    if (!baseImageRef.current) { setClosureStatus('idle'); return; }
    const om = getOverlayMetrics();
    if (!om) { setClosureStatus('idle'); return; }
    const { naturalW, naturalH, offX, offY, scale } = om as any;
    setClosureStatus('checking');
    await new Promise(r => setTimeout(r, 0)); // yield to paint

    // Downscale for performance
    const maxDim = 1024;
    const s = Math.min(1, maxDim / Math.max(naturalW, naturalH));
    const cw = Math.max(4, Math.round(naturalW * s));
    const ch = Math.max(4, Math.round(naturalH * s));

    // Mask canvas (black bg), white lines are walls
    const mask = document.createElement('canvas');
    mask.width = cw; mask.height = ch;
    const mctx = mask.getContext('2d');
    if (!mctx) { setClosureStatus('idle'); return; }
    mctx.fillStyle = '#000'; mctx.fillRect(0, 0, cw, ch);

    // Merge Blue Outline (pure blue pixels) as white walls
    try {
      const tcan = document.createElement('canvas');
      tcan.width = cw; tcan.height = ch;
      const tctx = tcan.getContext('2d');
      if (tctx) {
        tctx.drawImage(baseImageRef.current, 0, 0, cw, ch);
        const imgData = tctx.getImageData(0, 0, cw, ch);
        const data = imgData.data;
        const maskData = mctx.getImageData(0, 0, cw, ch);
        const md = maskData.data;
        for (let i = 0; i < data.length; i += 4) {
          const r = data[i]; const g = data[i + 1]; const b = data[i + 2];
          if (r === 0 && g === 0 && b === 255) {
            md[i] = 255; md[i + 1] = 255; md[i + 2] = 255; md[i + 3] = 255;
          }
        }
        mctx.putImageData(maskData, 0, 0);
      }
    } catch {}

    // Draw user strokes (mapped to scaled coords)
    mctx.lineCap = 'round';
    mctx.lineJoin = 'round';
    const natStroke = Math.max(2, Math.round((STROKE_WIDTH / Math.max(scale, 1e-6)) * s));
    mctx.lineWidth = natStroke;
    const mapPt = (p: { x: number; y: number }) => ({ x: ((p.x - offX) / scale) * s, y: ((p.y - offY) / scale) * s });
    const drawScaledStroke = (pts: { x: number; y: number }[], color: '#fff' | '#000') => {
      if (pts.length < 2) return;
      mctx.beginPath();
      const p0 = mapPt(pts[0]);
      mctx.moveTo(p0.x, p0.y);
      for (let i = 1; i < pts.length; i++) {
        const pi = mapPt(pts[i]);
        mctx.lineTo(pi.x, pi.y);
      }
      mctx.strokeStyle = color;
      mctx.stroke();
    };
    for (const sStroke of strokes) drawScaledStroke(sStroke.points, sStroke.mode === 'draw' ? '#fff' : '#000');
    if (currentStroke) drawScaledStroke(currentStroke.points, currentStroke.mode === 'draw' ? '#fff' : '#000');

    // Flood fill from borders to mark background reachable areas
    const gd = mctx.getImageData(0, 0, cw, ch);
    const buf = gd.data; // RGBA
    const passable = (idx: number) => buf[idx] === 0; // black pixel => empty
    const setVisited = (idx: number) => { buf[idx] = 1; buf[idx + 1] = 0; buf[idx + 2] = 0; buf[idx + 3] = 255; };
    const qX = new Uint16Array(cw * ch);
    const qY = new Uint16Array(cw * ch);
    let qh = 0, qt = 0;
    const push = (x: number, y: number) => { qX[qt] = x as any; qY[qt] = y as any; qt++; };
    const pop = () => { const x = qX[qh]; const y = qY[qh]; qh++; return [x, y] as [number, number]; };
    const idxAt = (x: number, y: number) => ((y * cw + x) << 2);

    // Seed from all border pixels that are black
    for (let x = 0; x < cw; x++) {
      let iTop = idxAt(x, 0);
      if (passable(iTop)) { setVisited(iTop); push(x, 0); }
      let iBot = idxAt(x, ch - 1);
      if (passable(iBot)) { setVisited(iBot); push(x, ch - 1); }
    }
    for (let y = 0; y < ch; y++) {
      let iL = idxAt(0, y);
      if (passable(iL)) { setVisited(iL); push(0, y); }
      let iR = idxAt(cw - 1, y);
      if (passable(iR)) { setVisited(iR); push(cw - 1, y); }
    }

    while (qh < qt) {
      const [x, y] = pop();
      // 4-neighbors
      const nbs = [
        [x + 1, y], [x - 1, y], [x, y + 1], [x, y - 1]
      ];
      for (const [nx, ny] of nbs) {
        if (nx < 0 || ny < 0 || nx >= cw || ny >= ch) continue;
        const ni = idxAt(nx, ny);
        if (passable(ni)) { setVisited(ni); push(nx, ny); }
      }
    }

    // Compute bounding box of wall pixels (white = 255,255,255)
    let minX = cw, minY = ch, maxX = -1, maxY = -1;
    for (let y = 0; y < ch; y++) {
      for (let x = 0; x < cw; x++) {
        const i = idxAt(x, y);
        if (buf[i] === 255 && buf[i + 1] === 255 && buf[i + 2] === 255) {
          if (x < minX) minX = x; if (x > maxX) maxX = x;
          if (y < minY) minY = y; if (y > maxY) maxY = y;
        }
      }
    }
    let bboxArea = cw * ch;
    if (maxX >= minX && maxY >= minY) {
      bboxArea = (maxX - minX + 1) * (maxY - minY + 1);
    }

    // Find largest connected enclosed region (black = 0) using BFS
    const isBlack = (idx: number) => buf[idx] === 0 && buf[idx + 1] === 0 && buf[idx + 2] === 0;
    const markComp = (idx: number) => { buf[idx] = 2; buf[idx + 1] = 0; buf[idx + 2] = 0; buf[idx + 3] = 255; };
    let largest = 0;
    const qX2 = new Uint16Array(cw * ch);
    const qY2 = new Uint16Array(cw * ch);
    for (let y = 0; y < ch; y++) {
      for (let x = 0; x < cw; x++) {
        const i = idxAt(x, y);
        if (isBlack(i)) {
          let size = 0;
          let h = 0, t = 0;
          qX2[t] = x as any; qY2[t] = y as any; t++;
          markComp(i);
          while (h < t) {
            const cx = qX2[h], cy = qY2[h]; h++;
            size++;
            const nbs2 = [[cx + 1, cy], [cx - 1, cy], [cx, cy + 1], [cx, cy - 1]] as const;
            for (const [nx, ny] of nbs2) {
              if (nx < 0 || ny < 0 || nx >= cw || ny >= ch) continue;
              const ni = idxAt(nx, ny);
              if (isBlack(ni)) { markComp(ni); qX2[t] = nx as any; qY2[t] = ny as any; t++; }
            }
          }
          if (size > largest) largest = size;
        }
      }
    }

  const ratio = bboxArea > 0 ? (largest / bboxArea) : 0;
  const passes = ratio >= CLOSURE_AREA_THRESHOLD && ratio >= CLOSURE_MIN_COMPONENT_RATIO;
  setClosureInfo({ enclosedArea: largest, areaRatio: ratio });
  setClosureStatus(passes ? 'closed' : 'open');

  }, [strokes, currentStroke, baseImageRef.current, getOverlayMetrics, STROKE_WIDTH]);

  if (!isOpen) return null;

  // When editing, render only the full-screen overlay (spinner or editor) to avoid flashing the base modal
  if (isEditMode) {
    return (
      <>
        {isOpen && isEditMode && selectedFile && overlayBaseUrl && (
          <div
            className="fixed inset-0 bg-black/70 flex items-center justify-center p-4"
            style={{ zIndex: 9999, touchAction: 'none', overscrollBehavior: 'contain' as any }}
            onWheel={(e) => { e.preventDefault(); e.stopPropagation(); }}
            onTouchMove={(e) => { e.preventDefault(); e.stopPropagation(); }}
            onTouchStart={(e) => { e.preventDefault(); e.stopPropagation(); }}
            aria-modal="true" role="dialog"
          >
            <div className="w-full max-w-[95vw] md:max-w-[1200px] h-[85vh] max-h-[90vh] bg-white rounded-lg shadow-2xl border border-gray-200 flex flex-col overflow-hidden">
              <div className="flex items-center justify-between p-3 bg-white border-b border-gray-200 select-none">
                <div className="flex items-center gap-3">
                  <span className="font-medium">Highlight the boundary</span>
                  <span className="text-sm text-gray-500">Draw over the boundary, then Extract</span>
                </div>
              </div>

              {/* Controls toolbar */}
              <div className="p-2 bg-gray-50 border-b border-gray-200 select-none">
                <div className="flex flex-wrap items-center gap-3">
                  {/* Zoom */}
                  <div className="flex items-center gap-2 text-xs text-gray-700">
                    <span className="whitespace-nowrap">Zoom</span>
                    <input
                      type="range"
                      min={0.25}
                      max={3}
                      step={0.05}
                      value={zoom}
                      onChange={(e) => setZoom(Number(e.target.value))}
                      className="w-32"
                    />
                    <button
                      className="px-2 py-1 border rounded"
                      onClick={() => {
                        if (!natW || !natH || !scrollWrapRef.current) return;
                        const rect = scrollWrapRef.current.getBoundingClientRect();
                        const fit = Math.min(rect.width / natW, rect.height / natH);
                        setZoom(Math.max(0.5, Math.min(3, fit)));
                      }}
                    >
                      Fit
                    </button>
                    <button
                      className="px-2 py-1 border rounded"
                      onClick={() => {
                        if (!natW || !scrollWrapRef.current) return;
                        const rect = scrollWrapRef.current.getBoundingClientRect();
                        const fitW = rect.width / natW;
                        setZoom(Math.max(0.5, Math.min(3, fitW)));
                      }}
                    >
                      Fit width
                    </button>
                    <button className="px-2 py-1 border rounded" onClick={() => setZoom(1)}>100%</button>
                  </div>

                  {/* Divider */}
                  <div className="w-px h-6 bg-gray-300" />

                  {/* Color picker & params */}
                  <div className="flex items-center gap-2 text-xs text-gray-700">
                    <label className="whitespace-nowrap">Line color</label>
                    <input
                      type="color"
                      value={options.red_hex || '#e74737'}
                      onChange={(e) => setOptions(prev => ({ ...prev, red_hex: e.target.value }))}
                      className="w-8 h-6 p-0 border rounded"
                      title="Pick boundary line color"
                    />
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-700">
                    <label className="whitespace-nowrap">Hue tol</label>
                    <input
                      type="number"
                      min={0}
                      max={30}
                      step={1}
                      value={options.hue_tolerance ?? 15}
                      onChange={(e) => setOptions(prev => ({ ...prev, hue_tolerance: Number(e.target.value) }))}
                      className="w-16 px-2 py-1 border rounded"
                    />
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-700">
                    <label className="whitespace-nowrap">Gap strength</label>
                    <input
                      type="number"
                      min={0}
                      max={4}
                      step={0.5}
                      value={options.gap_strength ?? 1}
                      onChange={(e) => setOptions(prev => ({ ...prev, gap_strength: Number(e.target.value) }))}
                      className="w-16 px-2 py-1 border rounded"
                    />
                  </div>

                  {/* Overlay original */}
                  <label className="inline-flex items-center gap-1 text-xs text-gray-700 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      className="mr-1"
                      checked={overlayOriginal}
                      onChange={(e) => setOverlayOriginal(e.target.checked)}
                    />
                    <span>Overlay original</span>
                  </label>

                  {/* Closure status */}
                  <div className="flex items-center gap-2 text-xs text-gray-700" title={`Requires ≥ ${(CLOSURE_AREA_THRESHOLD*100).toFixed(0)}% Inside, and ≥ ${(CLOSURE_MIN_COMPONENT_RATIO*100).toFixed(0)}% min region size` }>
                    <span
                      className={`px-2 py-0.5 rounded text-xs ${closureStatus === 'closed' ? 'bg-green-100 text-green-700 border border-green-200' : closureStatus === 'open' ? 'bg-yellow-100 text-yellow-800 border border-yellow-200' : 'bg-gray-100 text-gray-700 border border-gray-200'}`}
                      title={closureInfo ? `Enclosed area ~ ${(closureInfo.areaRatio * 100).toFixed(2)}%` : ''}
                    >
                      {closureStatus === 'checking' ? 'Checking…' : closureStatus === 'closed' ? 'Looks closed' : closureStatus === 'open' ? 'Likely open' : '—'}
                    </span>
                  </div>

                  {/* Spacer */}
                  <div className="flex-1" />

                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    <button onClick={handleCancelAll} className="px-3 py-1 text-xs rounded border hover:bg-gray-100">Cancel</button>
                    {/* Tool toggle */}
                    <div className="flex items-center gap-1" role="group" aria-label="Drawing tools">
                      <button
                        onClick={() => setTool('draw')}
                        className={`px-3 py-1 text-xs rounded border ${tool === 'draw' ? 'bg-blue-600 text-white border-blue-600' : 'hover:bg-gray-100'}`}
                        title="Draw mode"
                      >
                        <span className="inline-flex items-center gap-1"><Pencil className="h-3.5 w-3.5" /> Draw</span>
                      </button>
                      <button
                        onClick={() => setTool('erase')}
                        className={`px-3 py-1 text-xs rounded border ${tool === 'erase' ? 'bg-red-600 text-white border-red-600' : 'hover:bg-gray-100'}`}
                        title="Eraser mode"
                      >
                        <span className="inline-flex items-center gap-1"><Eraser className="h-3.5 w-3.5" /> Erase</span>
                      </button>
                    </div>
                    <button onClick={handleUndo} className="px-3 py-1 text-xs rounded border hover:bg-gray-100">Undo</button>
                    <button onClick={() => { setStrokes([]); setCurrentStroke(null); setTimeout(() => { checkClosure(); }, 0); }} className="px-3 py-1 text-xs rounded border hover:bg-gray-100">Clear</button>
                    <button
                      onClick={confirmMaskAndProceed}
                      disabled={closureStatus !== 'closed'}
                      className={`px-3 py-1 text-xs rounded text-white ${closureStatus === 'closed' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-400 cursor-not-allowed opacity-60'}`}
                      title={closureStatus !== 'closed' ? 'Boundary not closed yet' : 'Extract polygon'}
                    >
                      Extract
                    </button>
                  </div>
                </div>
              </div>
              <div ref={scrollWrapRef} className="flex-1 bg-neutral-900/90 p-3 overflow-auto">
                {/* Content sized by natural image x zoom for full access with scrollbars */}
                <div
                  ref={imageContainerRef}
                  className="relative select-none rounded-md border border-gray-700 bg-black"
                  style={{ width: natW && zoom ? natW * zoom : '100%', height: natH && zoom ? natH * zoom : '100%' }}
                >
                  {overlayOriginal && previewUrl && (
                    <img
                      src={previewUrl}
                      alt="Original"
                      className="absolute inset-0 w-full h-full select-none pointer-events-none"
                    />
                  )}
                  <img
                    ref={baseImageRef}
                    src={overlayBaseUrl}
                    alt="Blue Outline"
                    className="absolute inset-0 w-full h-full select-none pointer-events-none"
                    onLoad={(e) => {
                      try {
                        const el = e.currentTarget as HTMLImageElement;
                        if (el.naturalWidth && el.naturalHeight) {
                          setImgAspect(el.naturalWidth / el.naturalHeight);
                          setNatW(el.naturalWidth);
                          setNatH(el.naturalHeight);
                          // Compute fit zoom to initialize
                          if (scrollWrapRef.current) {
                            const rect = scrollWrapRef.current.getBoundingClientRect();
                            // Prefer fitting width so the image isn't too small initially
                            const fitW = rect.width / el.naturalWidth;
                            if (!initializedZoomRef.current) {
                              setZoom(Math.max(0.5, Math.min(3, fitW)));
                              initializedZoomRef.current = true;
                            }
                          }
                          // Start/refresh closure check once the image is ready
                          setTimeout(() => { try { checkClosure(); } catch {} }, 0);
                        }
                      } catch {}
                    }}
                  />
                  <canvas
                    ref={drawingCanvasRef}
                    className="absolute inset-0 z-10 cursor-crosshair"
                    style={{ touchAction: 'none' }}
                    onPointerDown={handlePointerDown}
                    onPointerMove={handlePointerMove}
                    onPointerUp={handlePointerUp}
                    onPointerCancel={(e) => { e.preventDefault(); e.stopPropagation(); endDraw(); }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
        {isOpen && isEditMode && selectedFile && !overlayBaseUrl && (
          <div
            className="fixed inset-0 bg-black/70 flex items-center justify-center p-4"
            style={{ zIndex: 9999 }}
            aria-modal="true" role="dialog"
          >
            <div className="w-full max-w-[600px] bg-white rounded-lg shadow-2xl border border-gray-200 p-4 select-none">
              <div className="flex items-center space-x-3">
                <svg className="animate-spin h-5 w-5 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path></svg>
                <span className="text-sm text-gray-700">Generating Blue Outline…</span>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header (no X close icon; use Cancel in footer) */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
            <p className="text-sm text-gray-600 mt-1">{description}</p>
          </div>
          {/* Intentionally no top-right X. Users should click Cancel below. */}
        </div>

        <div className="p-6 space-y-6">
          {/* File Upload Area */}
          {!selectedFile && (
            <div
              ref={dragRef}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              className="border-2 border-dashed border-gray-300 rounded-lg p-12 text-center hover:border-blue-400 transition-colors cursor-pointer"
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-lg font-medium text-gray-600 mb-2">
                Drop your map image here or click to browse
              </p>
              <p className="text-sm text-gray-500">
                Supports PNG, JPG, JPEG • Max size: 10MB
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={(e) => e.target.files?.[0] && handleFileSelect(e.target.files[0])}
                className="hidden"
              />
            </div>
          )}

          {/* Selected File Preview */}
          {selectedFile && previewUrl && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <ImageIcon className="h-5 w-5 text-blue-500" />
                  <span className="text-sm font-medium text-gray-700">
                    {selectedFile.name}
                  </span>
                  <span className="text-xs text-gray-500">
                    ({(selectedFile.size / 1024 / 1024).toFixed(1)} MB)
                  </span>
                </div>
                <button
                  onClick={resetState}
                  className="text-red-500 hover:text-red-700 transition-colors font-semibold text-lg leading-none"
                  aria-label="Remove selected file"
                  title="Remove selected file"
                >
                  ×
                </button>
              </div>

              {/* Quick Controls: Hue & Gap */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-sm font-medium text-gray-700 mb-3">Extraction Controls</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Hue Tolerance
                    </label>
                    <input
                      type="number"
                      min={0}
                      max={30}
                      step={1}
                      value={options.hue_tolerance ?? 15}
                      onChange={(e) => setOptions(prev => ({ ...prev, hue_tolerance: Number(e.target.value) }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">Wider hue band captures more of the selected line color.</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Gap Closing Strength
                    </label>
                    <input
                      type="number"
                      min={0}
                      max={4}
                      step={0.5}
                      value={options.gap_strength ?? 1}
                      onChange={(e) => setOptions(prev => ({ ...prev, gap_strength: Number(e.target.value) }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">Higher values aggressively bridge dotted gaps before extraction.</p>
                  </div>
                </div>
              </div>

              {/* Image Previews: Original + (conditional) Final */}
              {(() => {
                const hasPreview = !!(result && result.success && result.preview_image);
                return (
                  <div className={`grid grid-cols-1 ${hasPreview ? 'lg:grid-cols-2' : ''} gap-6`}>
                {/* Original Image with hover magnifier and click-to-pick */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-medium text-gray-700">Original Image</h4>
                    <button
                      type="button"
                      onClick={(e) => {
                        const next = !pickingEnabled;
                        setPickingEnabled(next);
                        if (!next) {
                          setShowLens(false);
                        } else {
                          // Show lens immediately only if the click is over the displayed image area
                          requestAnimationFrame(() => {
                            const m = getImageMetrics();
                            const container = imgContainerRef.current;
                            if (m && container) {
                              const rect = container.getBoundingClientRect();
                              // Mouse position relative to container
                              const rawCx = e.clientX - rect.left;
                              const rawCy = e.clientY - rect.top;
                              const insideImage = rawCx >= m.offX && rawCx <= m.offX + m.dispW && rawCy >= m.offY && rawCy <= m.offY + m.dispH;
                              if (insideImage) {
                                setShowLens(true);
                                drawLensAt(rawCx, rawCy);
                              } else {
                                // Keep lens hidden; it will appear when the cursor enters the image
                                setShowLens(false);
                              }
                            }
                          });
                        }
                      }}
                      className={`flex items-center space-x-1 px-2 py-1 text-xs rounded-md border ${pickingEnabled ? 'bg-blue-600 text-white border-blue-700' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
                      title={pickingEnabled ? 'Disable color picker' : 'Enable color picker'}
                    >
                      <Pipette className="h-3.5 w-3.5" />
                      <span>{pickingEnabled ? 'Picking…' : 'Pick color'}</span>
                    </button>
                  </div>
                  <div
                    ref={imgContainerRef}
                    className={`relative w-full h-64 border border-gray-200 rounded-lg bg-gray-50 overflow-hidden ${pickingEnabled ? 'cursor-crosshair' : 'cursor-default'}`}
                    onMouseMove={handleImageMouseMove}
                    onMouseEnter={() => pickingEnabled && setShowLens(true)}
                    onMouseLeave={handleImageMouseLeave}
                    onClick={handleImageClickPick}
                    title={pickingEnabled ? 'Hover to magnify; click to pick line color' : 'Click the pipette icon to enable picking'}
                  >
                    <img
                      ref={imgRef}
                      src={previewUrl}
                      alt="Upload preview"
                      className="absolute inset-0 w-full h-full object-contain select-none pointer-events-none"
                      onLoad={ensureSourceCanvasDrawn}
                    />
                    {showLens && (
                      <canvas
                        ref={lensCanvasRef}
                        width={LENS_SIZE}
                        height={LENS_SIZE}
                        style={{
                          position: 'absolute',
                          left: `${lensPos.x}px`,
                          top: `${lensPos.y}px`,
                          border: '2px solid rgba(59,130,246,0.8)',
                          borderRadius: '8px',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                          backgroundColor: 'white',
                          cursor: pickingEnabled ? 'crosshair' : 'default',
                          pointerEvents: 'none',
                          zIndex: 10
                        }}
                      />
                    )}
                  </div>
                  {/* Hidden source canvas for pixel sampling */}
                  <canvas ref={sourceCanvasRef} className="hidden" />
                  {/* Picked color display and instructions */}
                  <div className="mt-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Line Color (picked)
                    </label>
                    <div className="flex items-center space-x-3">
                      <span
                        className="inline-block w-8 h-8 rounded border"
                        style={{ backgroundColor: (options.red_hex ?? pickedHex ?? '#e74737') }}
                        aria-label="Picked color swatch"
                        title={options.red_hex ?? pickedHex ?? '#e74737'}
                      />
                      <input
                        type="text"
                        readOnly
                        value={options.red_hex ?? pickedHex ?? '#e74737'}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm bg-gray-100 cursor-default"
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">{pickingEnabled ? 'Hover within the image to magnify; click to pick the line color.' : 'Click the pipette to enable hover magnifier and color picking.'} Hue tolerance widens the band.</p>
                  </div>
                </div>
                {/* Extracted Preview - only show after extraction completes successfully */}
                {hasPreview && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Extracted Polygon</h4>
                    <img
                      src={result!.preview_image!}
                      alt="Polygon preview"
                      className="w-full h-64 object-contain border border-gray-200 rounded-lg bg-gray-50"
                    />
                  </div>
                )}
                  </div>
                );
              })()}
              
            </div>
          )}

          {/* Processing Status */}
          {isProcessing && (
            <div className="flex items-center justify-center py-8">
              <div className="flex items-center space-x-3">
                <Loader className="h-6 w-6 text-blue-500 animate-spin" />
                <span className="text-lg font-medium text-gray-700">Extracting polygon...</span>
              </div>
            </div>
          )}

          {/* Results */}
          {result && !isProcessing && (
            <div className="space-y-4">
              {result.success ? (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                    <div className="flex-1">
                      <h4 className="text-sm font-medium text-green-800 mb-2">
                        Polygon extracted successfully!
                      </h4>
                      {result.metadata && (
                        <div className="text-sm text-green-700 space-y-1">
                          <p>• {result.metadata.num_points} boundary points detected</p>
                          <p>• {result.num_contours_found} contours found in image</p>
                          <p>• Image size: {result.metadata.image_dimensions.width} × {result.metadata.image_dimensions.height} pixels</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <AlertCircle className="h-5 w-5 text-red-500 mt-0.5" />
                    <div>
                      <h4 className="text-sm font-medium text-red-800 mb-1">
                        Extraction failed
                      </h4>
                      <p className="text-sm text-red-700">{result.error}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center space-x-3">
            {result && result.success && (
              <button
                onClick={downloadPolygon}
                className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-blue-600 bg-white border border-blue-300 rounded-md hover:bg-blue-50 transition-colors"
              >
                <Download className="h-4 w-4" />
                <span>Download JSON</span>
              </button>
            )}
          </div>

          <div className="flex items-center space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>

            {result && result.success && (
              <button
                onClick={handleConfirm}
                className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 transition-colors"
              >
                <CheckCircle className="h-4 w-4" />
                <span>Use This Polygon</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PolygonExtractorModal;